import { Container, Sprite, Text, TextStyle } from "pixi.js";
import { UpgradeResourcePackage } from "../resources/UpgradeResourcePackage";
import { ProgressBar } from "@pixi/ui";
import { IBuildingModel } from "../../../common/models/Building/IBuildingModel";
import { gsap } from "gsap";
import { GameFont } from "../../../common/GameFont";
import { StringFormatExtention } from "../../../../../extensions/StringFormatExtention";
type Timeline = gsap.core.Timeline;

export class BuildingProgress extends Container {
    private static readonly COLLECT_POINTS_ANIMATION_TIME = 0.5;

    private readonly upgradeResources: UpgradeResourcePackage;
    private readonly buildingModel: IBuildingModel;

    private progressContainer: Container;
    private progressBar: ProgressBar;
    private textsContainer: Container;
    private progressText: Text;
    private levelFromText: Text;
    private levelToText: Text;
    private currentUpgradePointsBalance: number;

    public constructor(
        upgradeResources: UpgradeResourcePackage,
        buildingModel: IBuildingModel
    ) {
        super();
        this.upgradeResources = upgradeResources;
        this.buildingModel = buildingModel;
        this.build();
        this.layout();
        this.once("added", this.hide.bind(this));
    }

    private build(): void {
        this.textsContainer = new Container();
        this.addChild(this.textsContainer);
        this.buildTexts();

        this.progressContainer = new Container();
        this.addChild(this.progressContainer);
        this.buildProgressBar();

        this.currentUpgradePointsBalance = this.buildingModel.upgradePointsBalance.value;
        this.updateTextsPosition();
    }

    private buildProgressBar(): void {
        const progressBarBg = new Sprite(this.upgradeResources.assets_views_upgrade_progress_bar_progress_bar_bg_png);
        const progressBarFill = new Sprite(this.upgradeResources.assets_views_upgrade_progress_bar_progress_bar_fill_png);
        const currentProgressValue = this.buildingModel.upgradePointsBalance.value / this.buildingModel.requiredUpgradePoints.value * 100;
        this.progressBar = new ProgressBar({ bg: progressBarBg, fill: progressBarFill, progress: currentProgressValue });
        this.progressContainer.addChild(this.progressBar);
    }

    private buildTexts(): void {
        this.levelFromText = new Text(`RANK.${this.buildingModel.level.value}`, new ProgressTextStyle());
        this.textsContainer.addChild(this.levelFromText);
        this.progressText = new Text(`${StringFormatExtention.formatWithComma(this.buildingModel.upgradePointsBalance.value)}/${StringFormatExtention.formatWithComma(this.buildingModel.requiredUpgradePoints.value)} XP`, new ProgressTextStyle());
        this.progressText.anchor.x = 0.5;
        this.textsContainer.addChild(this.progressText);
        this.levelToText = new Text(`RANK.${this.buildingModel.nextLevel.value}`, new ProgressTextStyle());
        this.textsContainer.addChild(this.levelToText);
    }

    private layout(): void {
        this.progressText.position.x = this.progressBar.width / 2;
        this.levelToText.position.x = this.progressBar.width - this.levelToText.width;
        this.textsContainer.position.y = -this.textsContainer.height - 6;
    }

    public updateValues(): void {
        this.levelFromText.text = `RANK.${this.buildingModel.level.value}`;
        this.levelToText.text = `RANK.${this.buildingModel.nextLevel.value}`;
        this.updateProgressAnimation();
    }

    private updateTextsPosition(): void {
        const posOffset = this.progressText.width - this.progressBar.width + 95;
        if (posOffset > 0) {
            this.levelFromText.position.x = -this.progressBar.width * 0.43 - posOffset;
            this.levelToText.position.x = this.progressBar.width * 0.43 + posOffset;
        }
    }

    private async updateProgressAnimation(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        const target = { value: this.currentUpgradePointsBalance };
        animationTimeLine.to(target, { value: this.buildingModel.upgradePointsBalance.value, onUpdate: () => this.updateProgress(target.value), duration: BuildingProgress.COLLECT_POINTS_ANIMATION_TIME, ease: "none" });
        await animationTimeLine.play();
        animationTimeLine.kill();
        this.currentUpgradePointsBalance = this.buildingModel.upgradePointsBalance.value;
    }

    private updateProgress(value: number): void {
        this.progressText.text = `${StringFormatExtention.formatWithComma(Math.round(value))}/${StringFormatExtention.formatWithComma(this.buildingModel.requiredUpgradePoints.value)} XP`;
        this.progressBar.progress = value / this.buildingModel.requiredUpgradePoints.value * 100;
        this.updateTextsPosition();
    }

    private hide(): void {
        this.progressContainer.alpha = 0;
        this.textsContainer.alpha = 0;
    }

    public showAnimation(): gsap.core.Timeline {
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.set(this.progressContainer, { alpha: 1 });
        animationTimeLine.set(this.textsContainer, { alpha: 1 });
        animationTimeLine.fromTo(this.progressContainer.scale, { x: 0, y: 0 }, { x: 1, y: 1, duration: 0.8, ease: "elastic.out(1, 0.3)" });
        animationTimeLine.fromTo(this.progressText.scale, { x: 0, y: 0 }, { x: 1, y: 1, duration: 0.8, ease: "elastic.out(1, 0.4)" }, "<20%");
        animationTimeLine.fromTo(this.levelFromText.scale, { x: 0, y: 0 }, { x: 1, y: 1, duration: 0.8, ease: "elastic.out(1, 0.5)" }, "<");
        animationTimeLine.fromTo(this.levelToText.scale, { x: 0, y: 0 }, { x: 1, y: 1, duration: 0.8, ease: "elastic.out(1, 0.5)" }, "<");
        animationTimeLine.then(() => animationTimeLine.kill());
        return animationTimeLine;
    }
}

class ProgressTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 32
        }));
    }
}
